import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Events } from '@ngrx/signals/events'
import { EMPTY, of } from 'rxjs'
import { filter, map, switchMap } from 'rxjs/operators'

import { _Record } from '@app/core/record'
import { is, keys } from '@app/models/interactions/group/incoming'
import { conversationEvents } from '@app/modules/messaging/components/store/conversation.events'
import { MessagingService } from '@app/modules/messaging/services/messaging.service'
import { NotificationsStore } from '@app/overlays/notification/notifications.store'
import * as fromInteractionLists from '@interaction-lists/ngrx'

import * as fromFeature from '.'
import * as fromInteractionContext from '../interaction-context'
import * as fromNotifications from '../notifications'
import * as fromProfile from '../profile'

const UNKNOWN_ID = -1

@Injectable()
export class UnreadInteractionEffects {
  private readonly actions$ = inject(Actions)
  private readonly notificationsStore = inject(NotificationsStore)
  private readonly events = inject(Events)
  private readonly messagingService = inject(MessagingService)

  readonly fromInteractionContextLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromInteractionContext.Actions.loaded),
      map(({ data }) =>
        fromFeature.Actions.addBatch({
          partial: {
            ..._Record.fromKeysWithMap(keys, key => (data.hasUnread[key] ? [UNKNOWN_ID] : [])),
            messages: data.messages_dot_ids,
          },
        }),
      ),
    ),
  )

  readonly fromInteractionListsLoadSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromInteractionLists.Actions.loadSuccess),
      filter(({ group }) => is(group)),
      switchMap(({ data, unreadGroup }) => [
        fromFeature.Actions.removeBatch({ partial: { [unreadGroup]: [UNKNOWN_ID] } }),
        fromFeature.Actions.addBatch({
          partial: { [unreadGroup]: data.unviewed },
        }),
      ]),
    ),
  )

  readonly hide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProfile.Actions.hide),
      map(({ id }) => fromFeature.Actions.removeBatch({ partial: _Record.fromKeys(keys, [id]) })),
    ),
  )

  readonly receivedNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromNotifications.Actions.received),
      map(note => ({ ...note, group: this.notificationsStore.toGroup(note.notification) })),
      switchMap(({ group, notification }) => {
        const conversationWithProfileIsOpen = this.messagingService.isConversationOpen(
          notification.sref_params.profile_browse_id,
        )

        return group !== 'messages' || !conversationWithProfileIsOpen
          ? of(fromFeature.Actions.addSingle({ group, id: notification.source_profile_id }))
          : EMPTY
      }),
    ),
  )

  readonly onConversationLoaded$ = createEffect(() =>
    this.events.on(conversationEvents.loadSuccess).pipe(
      map(({ payload }) => payload.response.conversation.profile.id),
      map(id => fromFeature.Actions.removeSingle({ group: 'messages', id })),
    ),
  )

  readonly onGranted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProfile.Actions.accessGranted),
      map(({ id }) => fromFeature.Actions.removeSingle({ id, group: 'pending_requesters' })),
    ),
  )
}
